import { useEffect, useRef, useState } from "react";

import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AppCard from "@plannel/components/ui/AppCard";
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import SaveIcon from '@mui/icons-material/Save';
import InputAdornment from '@mui/material/InputAdornment';
import { grey } from '@mui/material/colors';

import { withTranslation } from "react-i18next";
import { cloneDeep, isEmpty, isEqual, isNull } from "lodash";

import appConfigService from "@plannel/services/system/app-config-service";
import systemService from "@plannel/services/system/system-service";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import StyledSwitch from "@plannel/components/ui/StyledSwitch"
import PasswordPolicyContainer from "@plannel/components/PasswordPolicyContainer";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

const CONFIG_CODES = {
  MAX_FAILURE_COUNT: "MAX_FAILURE_COUNT",
  ACCOUNT_EXP_PERIOD: "ACCOUNT_EXP_PERIOD",
  PASSWORD_EXP_PERIOD: "PASSWORD_EXP_PERIOD"
};

const SignInPolicy = ({ t }) => {
  const defaultActive = { maxFailureCount: false, longTermUnvisitedDays: false, maxPasswordDays: false };
  const [active, setActive] = useState(defaultActive);

  const defaultValues = useRef({ maxFailureCount: "", longTermUnvisitedDays: "", maxPasswordDays: "" });
  const [values, setValues] = useState(defaultValues.current);
  const defaultAppConfigs = [
    { configCd: CONFIG_CODES.MAX_FAILURE_COUNT },
    { configCd: CONFIG_CODES.ACCOUNT_EXP_PERIOD },
    { configCd: CONFIG_CODES.PASSWORD_EXP_PERIOD },
  ];
  const [appConfigs, setAppConfigs] = useState([]);
  const [errors, setErrors] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const { snackBarMessageColor } = ColorUtil;

  const setValuesAndActiveAndAppConfigs = configs => {
    const appConfigValues = cloneDeep(defaultValues.current);
    const appConfigActive = cloneDeep(defaultActive);

    for (const config of configs) {
      const { configCd, numVal, activeFlg } = config;
      const notNullValue = numVal ? numVal : "";
      if (isEqual(configCd, CONFIG_CODES.MAX_FAILURE_COUNT)) {
        appConfigValues.maxFailureCount = notNullValue;
        appConfigActive.maxFailureCount = activeFlg;
      } else if (isEqual(configCd, CONFIG_CODES.ACCOUNT_EXP_PERIOD)) {
        appConfigValues.longTermUnvisitedDays = notNullValue;
        appConfigActive.longTermUnvisitedDays = activeFlg;
      } else if (isEqual(configCd, CONFIG_CODES.PASSWORD_EXP_PERIOD)) {
        appConfigValues.maxPasswordDays = notNullValue;
        appConfigActive.maxPasswordDays = activeFlg;
      }
    }

    setValues(appConfigValues);
    setActive(appConfigActive);
    setAppConfigs(configs);
  }

  const retrieveSignInPolicy = () => {
    const codes = Object.values(CONFIG_CODES);
    appConfigService.getAppConfigs({ configCds: codes })
      .then(response => {
        const { data } = response;

        if (isEmpty(data)) {
          setAppConfigs(defaultAppConfigs);
          setValues(defaultValues.current);
          setActive({ maxFailureCount: true, longTermUnvisitedDays: true, maxPasswordDays: true });
        } else {
          setValuesAndActiveAndAppConfigs(data);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveDefaultLoginPolicies = () => {
    systemService.getDefaultLoginPolicies()
      .then(response => {
        const { loginPolicies } = response.data;
        defaultValues.current = loginPolicies;
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        retrieveSignInPolicy();
      });
  }

  const isInvalid = value => {
    return isNull(value) || isEqual(value, "") || isNaN(Number(value));
  }

  const checkValidation = () => {
    let isValid = true;
    const invalidValues = {};

    for (const key of Object.keys(values)) {
      if (active[key] && isInvalid(values[key])) {
        isValid = false;
        invalidValues[key] = true;
      }
    }

    setErrors({ ...errors, ...invalidValues });

    return isValid;
  }

  const getEnteredValues = code => {
    const enteredValues = {};

    if (isEqual(code, CONFIG_CODES.MAX_FAILURE_COUNT)) {
      return { activeFlg: active.maxFailureCount, numVal: values.maxFailureCount };
    } else if (isEqual(code, CONFIG_CODES.ACCOUNT_EXP_PERIOD)) {
      return { activeFlg: active.longTermUnvisitedDays, numVal: values.longTermUnvisitedDays };
    } else if (isEqual(code, CONFIG_CODES.PASSWORD_EXP_PERIOD)) {
      return { activeFlg: active.maxPasswordDays, numVal: values.maxPasswordDays };
    }

    return enteredValues;
  }

  const getSaveData = () => {
    const saveData = [];
    for (const config of appConfigs) {
      const enteredConfig = getEnteredValues(config.configCd);

      saveData.push({ ...config, ...enteredConfig });
    }

    return saveData;
  }

  const saveSignInPolicy = () => {
    if (!checkValidation()) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterNumber") });
      return;
    }

    const saveData = getSaveData();
    appConfigService.upsertAppConfig(saveData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        retrieveSignInPolicy();
      });
  }

  const handleDialog = (action, _falseAction) => {
    if (action === "SAVE") {
      saveSignInPolicy();
    }
  };

  const handleSwitch = e => {
    const { name, checked } = e.target;

    setActive({
      ...active,
      [name]: checked
    });

    if (!checked) {
      setValues({
        ...values,
        [name]: ""
      });
    }
  }

  const handleValues = e => {
    const { name, value } = e.target;

    setValues({
      ...values,
      [name]: isInvalid(value.trim()) ? "" : value.trim()
    });

    const checkError = Boolean(errors[name]);

    if (checkError) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  }

  const colorByActive = activeFlg => {
    return { color: activeFlg ? "black" : grey[500] }
  }

  const inputPropsByValue = value => {
    return value ? { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>{t("dayUnit")}</InputAdornment> } : {};
  }

  useEffect(() => {
    retrieveDefaultLoginPolicies();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 정책 1행: 제목 + 토글, 그 아래 입력값 + 기본값 안내
  const renderPolicy = (name, title, fieldLabel, defaultVal, withUnit) => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Typography sx={{ fontSize: FS.H1, fontWeight: 600, ...colorByActive(active[name]) }}>{title}</Typography>
        <StyledSwitch size="small" name={name} checked={active[name]} onChange={handleSwitch} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5 }}>
        <TextField
          size="small"
          name={name}
          label={fieldLabel}
          value={values[name]}
          sx={{ maxWidth: 220 }}
          InputProps={withUnit ? inputPropsByValue(values[name]) : undefined}
          required={active[name]}
          error={Boolean(errors[name]) && active[name]}
          disabled={!active[name]}
          onChange={handleValues}
        />
        <Typography variant="caption" sx={{ color: grey[500] }}>{`(${t("defaultValue", { value: defaultVal })})`}</Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Box id={"app-security-settings"} sx={{ height: "100%", overflow: "auto", px: 3, py: 2 }}>
        <Typography sx={{ fontWeight: 700 }} variant="h5">{t("signInPolicy")}</Typography>
        <Divider sx={{ borderColor: "primary.main", borderBottomWidth: 2, mt: 1, mb: 3 }} />

        <Box sx={{ maxWidth: 760 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">{t("MSG_LimitPasswordInput")}</Typography>
            <Typography variant="body2" color="text.secondary">{t("MSG_LockAccount")}</Typography>
            <Typography variant="body2" color="text.secondary">{t("MSG_SetExpirationPeriod")}</Typography>
          </Box>

          <AppCard>
            <CardContent sx={{ p: 2.5 }}>
              {renderPolicy("maxFailureCount", t("limitPasswordInput"), t("maxNumInput"), 5, false)}
              <Divider sx={{ my: 2.5 }} />
              {renderPolicy("longTermUnvisitedDays", t("setAccountExpiredPeriod"), t("expPeriod"), 365, true)}
              <Divider sx={{ my: 2.5 }} />
              {renderPolicy("maxPasswordDays", t("setPasswordExpiredPeriod"), t("expPeriod"), 180, true)}
            </CardContent>
          </AppCard>

          <Box sx={{ mt: 2.5 }}>
            <Button size="large" color="primary" variant="contained" disableElevation startIcon={<SaveIcon />}
              onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })}>{t("save")}</Button>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <PasswordPolicyContainer />
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(SignInPolicy);
